/**
 * Build-time configuration.
 *
 * Vite replaces `import.meta.env.VITE_*` with string literals when it builds,
 * so everything here is baked into the bundle and is public by definition.
 * Nothing secret belongs in this file or in the .env it reads; see
 * .env.example for what each variable is and where to get it.
 */

function str(value: string | undefined): string {
  return (value ?? '').trim();
}

/** Strip trailing slashes so `${base}/v1/analyses` never doubles up. */
function base(value: string | undefined): string {
  return str(value).replace(/\/+$/, '');
}

export const env = {
  /** Cloud Run service A. Empty when the deployment forgot to set it. */
  apiBaseUrl: base(import.meta.env.VITE_API_BASE_URL),

  firebase: {
    apiKey: str(import.meta.env.VITE_FIREBASE_API_KEY),
    authDomain: str(import.meta.env.VITE_FIREBASE_AUTH_DOMAIN),
    projectId: str(import.meta.env.VITE_FIREBASE_PROJECT_ID),
    storageBucket: str(import.meta.env.VITE_FIREBASE_STORAGE_BUCKET),
    messagingSenderId: str(import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID),
    appId: str(import.meta.env.VITE_FIREBASE_APP_ID),
    measurementId: str(import.meta.env.VITE_FIREBASE_MEASUREMENT_ID),
  },

  /** Origin used to build share links. Defaults to wherever the page is served
   *  from, which is correct for Firebase Hosting and for local development. */
  publicBaseUrl:
    base(import.meta.env.VITE_PUBLIC_BASE_URL) ||
    (typeof window === 'undefined' ? '' : window.location.origin),

  /** Local emulator suite. Must never be on in a deployed build. */
  useFirebaseEmulators: str(import.meta.env.VITE_USE_FIREBASE_EMULATORS) === '1',
} as const;

/** True when the API base URL was supplied at build time. */
export const apiConfigured = env.apiBaseUrl.length > 0;

/** True when enough Firebase configuration exists to sign a user in. */
export const firebaseConfigured =
  env.firebase.apiKey.length > 0 &&
  env.firebase.authDomain.length > 0 &&
  env.firebase.projectId.length > 0 &&
  env.firebase.appId.length > 0;

/**
 * Fallbacks for the service limits, used only until `GET /health` answers
 * (and for good if the API is unreachable). They restate the documented public
 * caps; the API is the authority and its numbers win as soon as they arrive.
 */
export const FALLBACK_LIMITS = {
  /** Documents per job. The VOS layout is O(n^2) per iteration over items, but
   *  parsing and counting scale with documents, so both are capped. */
  maxDocuments: 5000,
  /** Items on a map. This is the one that bounds the layout cost. */
  maxItems: 2000,
  /** Upload size. Must match the API's MAX_UPLOAD_BYTES default (20 MiB).
   *
   *  These are only used until GET /health answers; the API is the authority
   *  and overwrites them. But a fallback that disagrees with the server is a
   *  fallback that lies: this one said 32 MiB while the API refused anything
   *  over 20, so on a slow or failed limits fetch the UI would accept a file
   *  and then have it rejected on submit. Keep the two numbers equal. */
  maxUploadBytes: 20 * 1024 * 1024,
} as const;
